import React, { createContext, useContext, useReducer, useMemo, ReactNode, Dispatch } from 'react';
import { ProjectData } from '@/types/project';
import { initialProjectData } from '@/constants/initialData';

// Define the structure of the project state
interface ProjectState {
  readonly data: ProjectData; // State is immutable by default
  readonly isDirty: boolean;
}

// Define all possible actions
type ProjectAction = 
  | { type: 'UPDATE_DATA'; payload: Partial<ProjectData> }
  | { type: 'CLEAR_DATA' }
  | { type: 'MARK_SAVED' };

// Allow dynamic initialization of the state
const getInitialState = (): ProjectState => ({
  data: initialProjectData,
  isDirty: false,
});

// Reducer function to manage state transitions
const projectReducer = (state: ProjectState, action: ProjectAction): ProjectState => {
  switch (action.type) {
    case 'UPDATE_DATA':
      return {
        data: { ...state.data, ...action.payload },
        isDirty: true,
      };
    case 'CLEAR_DATA':
      return getInitialState();
    case 'MARK_SAVED':
      return {
        ...state,
        isDirty: false,
      };
    default:
      // Enforce strict typing by making this case unreachable
      throw new Error(`Unhandled action type: ${(action as never).type}`);
  }
};

// Define the context type for better TypeScript inference
interface ProjectContextType {
  state: ProjectState;
  dispatch: Dispatch<ProjectAction>;
}

// Create context with a strict null check
const ProjectContext = createContext<ProjectContextType | null>(null);

// Provide the project state and dispatcher to children components
export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(projectReducer, undefined, getInitialState);

  // Memoize context value to avoid unnecessary re-renders
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};

// Custom hook to consume project context
export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
